import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';

const PENDING_MODAL_PREFIX = 'cne-open-modal-';
const GLOBAL_MODAL_STORAGE_KEY = 'cne-open-community-newsletter';
const CUSTOMER_POSTED_PARAM = 'customer_posted';

/**
 * Newsletter signup with native form POST (Shopify hCaptcha) and chapter preview modal on return.
 *
 * @extends {Component}
 */
class CommunityNewsletterSection extends Component {
  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    super.connectedCallback();

    this.#form = this.querySelector('form.community-newsletter-editorial__form');

    if (this.dataset.useModal === 'true') {
      if (this.#form) {
        this.#form.addEventListener('submit', this.#formSubmitHandler);
      }
      void this.#maybeOpenModalAfterReturn();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#form?.removeEventListener('submit', this.#formSubmitHandler);
  }

  #formSubmitHandler = (event) => {
    this.#onSubmit(event);
  };

  /**
   * @param {SubmitEvent} event
   */
  #onSubmit(event) {
    event.preventDefault();

    const form = this.#form;
    if (!form || form.dataset.submitting === 'true') return;

    const submitButton = form.querySelector('.community-newsletter-editorial__button');

    this.#clearMessages();
    form.dataset.submitting = 'true';
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

    this.#markPendingModal();

    const submitNative = () => {
      form.submit();
    };

    if (typeof window.Shopify?.captcha?.protect === 'function') {
      window.Shopify.captcha.protect(form, submitNative);
    } else {
      submitNative();
    }
  }

  async #maybeOpenModalAfterReturn() {
    if (!this.#shouldOpenPreviewModal()) return;

    this.#clearPendingModal();
    this.#clearGlobalModalStorage();
    this.#stripSuccessQueryParams();
    await this.#openPreviewModal();
  }

  #shouldOpenPreviewModal() {
    if (this.dataset.openOnSuccess === 'true') return true;

    if (this.#hasVisibleFormError()) return false;

    if (this.#isShopifyCustomerPostedReturn()) return true;

    if (this.#globalStorageMatchesThisModal()) return true;

    const key = this.#pendingModalKey();
    if (key && sessionStorage.getItem(key) === '1' && !this.#hasVisibleFormError()) {
      return true;
    }

    return false;
  }

  #globalStorageMatchesThisModal() {
    try {
      const storedId = sessionStorage.getItem(GLOBAL_MODAL_STORAGE_KEY);
      return Boolean(storedId && storedId === this.dataset.modalId);
    } catch {
      return false;
    }
  }

  #clearGlobalModalStorage() {
    try {
      sessionStorage.removeItem(GLOBAL_MODAL_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  /**
   * @returns {boolean}
   */
  #isShopifyCustomerPostedReturn() {
    const params = new URLSearchParams(window.location.search);
    if (params.get(CUSTOMER_POSTED_PARAM) !== 'true') return false;

    return this.#hashMatchesThisForm();
  }

  /**
   * @returns {boolean}
   */
  #hashMatchesThisForm() {
    const hash = window.location.hash.replace(/^#/, '');
    const formId = this.#form?.id;
    const modalId = this.dataset.modalId;

    if (!hash) return true;
    if (formId && (hash === formId || hash.includes(formId))) return true;
    if (modalId && (hash === modalId || hash.includes(modalId))) return true;
    if (hash.startsWith('CommunityNewsletter')) return true;

    return false;
  }

  #hasVisibleFormError() {
    const errorEl = this.querySelector('[data-newsletter-error]');
    return Boolean(errorEl && !errorEl.hidden && errorEl.textContent?.trim());
  }

  #stripSuccessQueryParams() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(CUSTOMER_POSTED_PARAM)) return;

    url.searchParams.delete(CUSTOMER_POSTED_PARAM);
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, '', next);
  }

  #pendingModalKey() {
    const sectionId = this.dataset.sectionId;
    return sectionId ? `${PENDING_MODAL_PREFIX}${sectionId}` : null;
  }

  #markPendingModal() {
    const key = this.#pendingModalKey();
    const modalId = this.dataset.modalId;
    try {
      if (key) sessionStorage.setItem(key, '1');
      if (modalId) sessionStorage.setItem(GLOBAL_MODAL_STORAGE_KEY, modalId);
    } catch {
      /* ignore */
    }
  }

  #clearPendingModal() {
    const key = this.#pendingModalKey();
    try {
      if (key) sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }

  async #openPreviewModal() {
    const modalId = this.dataset.modalId;
    if (!modalId) return;

    await customElements.whenDefined('dialog-component');

    const tryOpen = () => {
      const host = document.getElementById(modalId);
      if (!host) return false;

      if (host instanceof DialogComponent) {
        host.showDialog();
        return true;
      }

      if ('showDialog' in host && typeof host.showDialog === 'function') {
        host.showDialog();
        return true;
      }

      const dialog = host.querySelector('dialog');
      if (dialog && typeof dialog.showModal === 'function') {
        dialog.showModal();
        return true;
      }

      return false;
    };

    if (tryOpen()) return;

    requestAnimationFrame(() => {
      if (!tryOpen()) {
        setTimeout(tryOpen, 150);
        setTimeout(tryOpen, 500);
      }
    });
  }

  #clearMessages() {
    const errorEl = this.querySelector('[data-newsletter-error]');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }
}

if (!customElements.get('community-newsletter-editorial')) {
  customElements.define('community-newsletter-editorial', CommunityNewsletterSection);
}
